import { toast } from 'sonner';
import { createMutationHook } from '@/hooks/lib/createMutationHook';
import { apiPost, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';

/**
 * Response shape returned by `POST /users/:id/cancel-invitation`.
 * The backend atomically expires every outstanding password-reset token
 * for the user AND soft-deletes the invited user row, so a successful
 * response means the link is dead AND the row will no longer appear in
 * the users list once the cached query refetches.
 */
export interface CancelInvitationResponse {
  userId: string;
  email: string;
  cancelled: true;
}

/**
 * Cancel a pending invitation. Two server-side side effects fire
 * inside one transaction:
 *   1. All unconsumed password-reset tokens for the user are expired
 *      (the link in the previously-sent email returns
 *      "invalid or expired" when clicked).
 *   2. The AuthUser row is soft-deleted (`status = 'deleted'`,
 *      `deletedAt = now()`) — the user disappears from the listing.
 *
 * The backend rejects with 400 when the user has already accepted the
 * invite (status !== 'invited'). The FE only renders the menu item for
 * invited users, but the server-side check remains the source of truth.
 */
export const useCancelInvitation = createMutationHook<CancelInvitationResponse, string>({
  mutationFn: (userId) =>
    apiPost<CancelInvitationResponse, undefined>(
      operationsClient,
      settingsEndpoints.cancelInvitation(userId),
      undefined,
    ),
  onSuccess: (_data, userId, queryClient) => {
    // Invalidate the users list so the soft-deleted row drops out of
    // the table — the list endpoint already filters on
    // `membershipStatus != deleted`, so refetching is all we need.
    void queryClient.invalidateQueries({ queryKey: settingsKeys.users() });
    // Also drop the open user-detail card (userDetail(userId)) for the
    // now-deleted user so it doesn't linger until a hard refresh.
    void queryClient.invalidateQueries({ queryKey: settingsKeys.userDetail(userId) });
    toast.success('Invitation cancelled');
  },
});
